import { useQuery, useMutation, useQueryClient, QueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type { InventoryItem } from '../types';

async function batchPut(
  updates: { stickerId: string; quantity: number }[],
  queryClient: QueryClient,
  onProgress?: (done: number, total: number) => void
) {
  const CHUNK = 10;
  let done = 0;
  for (let i = 0; i < updates.length; i += CHUNK) {
    await Promise.all(
      updates.slice(i, i + CHUNK).map((u) =>
        api.put(`/inventory/${u.stickerId}`, { quantity: u.quantity })
      )
    );
    done = Math.min(i + CHUNK, updates.length);
    onProgress?.(done, updates.length);
  }
  queryClient.invalidateQueries({ queryKey: ['inventory'] });
}

export function useInventory() {
  return useQuery<InventoryItem[]>({
    queryKey: ['inventory'],
    queryFn: async () => {
      const { data } = await api.get('/inventory');
      // API returns { data: { items: [...], count: N } }
      return data.data?.items ?? [];
    },
  });
}

export function useUpdateInventory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { stickerId: string; quantity: number }) => {
      const { data } = await api.put(`/inventory/${payload.stickerId}`, { quantity: payload.quantity });
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export function usePhotoUpload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { stickerId: string; imageBase64: string; contentType: string }) => {
      const { data } = await api.post(`/inventory/${payload.stickerId}/photo`, {
        imageBase64: payload.imageBase64,
        contentType: payload.contentType,
      });
      return data.data as { stickerId: string; photoUrl: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export function useBatchUpdateInventory() {
  const queryClient = useQueryClient();
  return (
    updates: { stickerId: string; quantity: number }[],
    onProgress?: (done: number, total: number) => void
  ) => batchPut(updates, queryClient, onProgress);
}

export function usePhotoDelete() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (stickerId: string) => {
      const { data } = await api.delete(`/inventory/${stickerId}/photo`);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}
